import { useState, useEffect } from "react";
import CallDialer from "./CallDialer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  sent: { label: "📤 Sent", color: "#9CA3AF" },
  delivered: { label: "📬 Delivered", color: "#5AC8D8" },
  opened: { label: "👁️ Opened", color: "#FFB347" },
  clicked: { label: "🖱️ Clicked", color: "#00DC78" },
  bounced: { label: "⚠️ Bounced", color: "#FCA5A5" },
  send_failed: { label: "⚠️ Send Failed", color: "#FCA5A5" },
  queued: { label: "⏳ Queued", color: "#9CA3AF" },
};

const LeadPaintRosterTab = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firmName, setFirmName] = useState("");
  const [firmEmail, setFirmEmail] = useState("");
  const [firmPhone, setFirmPhone] = useState("");
  const [tradeCategory, setTradeCategory] = useState("lead_paint");
  const [trades, setTrades] = useState([]);
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [trainOpen, setTrainOpen] = useState(true);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dialerCtx, setDialerCtx] = useState(null);
  const [seedStatus, setSeedStatus] = useState("");
  const [seedResult, setSeedResult] = useState(null);

  const seedVendorScripts = async () => {
    if (seedStatus === "seeding") return;
    setSeedStatus("seeding");
    setSeedResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai/scripts/seed-vendor-outreach-pack`, {
        method: "POST", credentials: "include"
      });
      const d = await r.json();
      if (r.ok) {
        setSeedResult(d);
        setSeedStatus("done");
        setTimeout(() => setSeedStatus(""), 6000);
      } else {
        setSeedResult({ error: d.detail || "Could not seed scripts." });
        setSeedStatus("done");
      }
    } catch (e) {
      setSeedResult({ error: "Network error. Try again." });
      setSeedStatus("done");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/outreach/lead-paint-invites`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/outreach/sub-vendor-trades`, { credentials: "include" }),
      ]);
      if (r1.ok) {
        const d = await r1.json();
        setInvites(d.invites || []);
      }
      if (r2.ok) {
        const d2 = await r2.json();
        setTrades(d2.trades || []);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openDialer = (inv) => {
    setDialerCtx({
      title: `Call ${inv.firm_name}`,
      subtitle: `${inv.trade_label || "Sub-Vendor"} · ${inv.firm_email}`,
      phone: inv.firm_phone || "",
      email: inv.firm_email || "",
      context: { type: "lead_paint", record_id: inv.invite_id, record_label: inv.firm_name },
      talking_points: [
        "Hi, this is Shannon Ware from Moovin Forward — I sent you a sub-vendor roster invite a few days back.",
        "We restore vacant units in DFW for landlords, REO portfolios, and HUD-VASH veteran placements.",
        "I'm building a vetted roster of trade partners I can hand directly to property owners — no referral fee, you contract with the owner.",
        "Just need to confirm your service area, certifications, insurance, and turnaround so I can place you on the roster with confidence.",
      ],
      qa: [
        { q: "Do you take a referral fee?", a: "No. The owner contracts with you directly. We just hand them a vetted roster." },
        { q: "What's your typical job size?", a: "Single-family + small multi-family unit turnovers in DFW. Typically a $500-$5,000 scope per unit." },
        { q: "How fast do you need turnaround?", a: "Most stabilizations need 7-14 day turnaround on sub-trade work. We coordinate scheduling around the rest of the turnover." },
        { q: "Are you certified / insured?", a: "Yes — and I expect you to be too. Send me your COI + certification when you have a moment." },
        { q: "When can we start?", a: "Pilot launches in August 2026. Want to lock you in now so you're on the first roster I hand out." },
      ],
    });
    setDialerOpen(true);
  };

  const sendInvite = async () => {
    // Defensive DOM re-read — mobile Chrome autofill can populate the visual
    // field without firing React onChange. If our state says empty but the
    // DOM has a value, trust the DOM.
    let effName = firmName.trim();
    let effEmail = firmEmail.trim();
    let effPhone = firmPhone.trim();
    try {
      const domName = document.querySelector('[data-testid="lp-firm-name"]');
      const domEmail = document.querySelector('[data-testid="lp-firm-email"]');
      const domPhone = document.querySelector('[data-testid="lp-firm-phone"]');
      if (!effName && domName && domName.value) effName = domName.value.trim();
      if (!effEmail && domEmail && domEmail.value) effEmail = domEmail.value.trim();
      if (!effPhone && domPhone && domPhone.value) effPhone = domPhone.value.trim();
    } catch { /* ignore */ }

    if (!effName || !effEmail || !effEmail.includes("@")) {
      const nameLabel = effName ? `"${effName}"` : "(empty)";
      const emailLabel = effEmail ? `"${effEmail}"` : "(empty)";
      setToast({ ok: false, msg: `Firm name + valid firm email required. Got name=${nameLabel} email=${emailLabel}` });
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/outreach/lead-paint-invite`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firm_name: effName,
          firm_email: effEmail,
          firm_phone: effPhone,
          notes: notes.trim(),
          trade_category: tradeCategory,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setToast({ ok: true, msg: `Invite sent to ${d.actually_sent_to}. Tracking armed.` });
        setFirmName(""); setFirmEmail(""); setFirmPhone(""); setNotes("");
        load();
      } else {
        setToast({ ok: false, msg: d.detail || "Send failed." });
      }
    } catch (e) {
      setToast({ ok: false, msg: "Network error." });
    }
    setSending(false);
    setTimeout(() => setToast(null), 7000);
  };

  return (
    <div data-testid="lead-paint-roster-tab" style={{ display: "grid", gap: "1.1rem" }}>
      <div>
        <h2 style={{ color: "#F3F4F6", margin: 0, fontSize: "1.5rem" }}>🛡️ Sub-Vendor Roster</h2>
        <p style={{ color: "#9CA3AF", marginTop: 4, fontSize: "0.92rem" }}>
          Build vetted trade partners across <strong>12 categories</strong> — lead-paint, flooring, paint, plumbing, HVAC, locksmith, carpet, pest, asbestos, mold, electrician, blinds. Send warm partnership invites, track delivery + opens + clicks, dial them up with talking points pre-loaded.
        </p>
      </div>

      {/* Training card */}
      <div data-testid="lp-train-card" style={{
        background: "linear-gradient(135deg, rgba(122,143,126,0.14) 0%, rgba(27,42,78,0.08) 100%)",
        border: "1px solid rgba(122,143,126,0.4)", borderLeft: "4px solid #7a8f7e",
        borderRadius: 8, padding: "1rem 1.2rem"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setTrainOpen(!trainOpen)}>
          <div>
            <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#86EFAC", fontWeight: 700 }}>📖 Walk Me Through It</div>
            <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "1rem", marginTop: 3 }}>How the Lead-Paint Roster works</div>
          </div>
          <button type="button" style={{ background: "transparent", border: "1px solid #86EFAC", color: "#86EFAC", padding: "0.35rem 0.75rem", borderRadius: 4, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}>
            {trainOpen ? "Hide" : "Show"}
          </button>
        </div>
        {trainOpen && (
          <div style={{ marginTop: "0.8rem", color: "#D1D5DB", fontSize: "0.88rem", lineHeight: 1.55 }}>
            <p style={{ marginTop: 0 }}><strong style={{ color: "#86EFAC" }}>Why this matters:</strong> A large share of DFW affordable housing is pre-1978. Any unit we touch in that bucket is required by EPA RRP rules to have lead-paint testing/abatement done by a certified firm. We don&rsquo;t do that work in-house &mdash; we INTRODUCE the property owner directly to a vetted sub-vendor.</p>
            <p><strong style={{ color: "#86EFAC" }}>What you&rsquo;re sending:</strong> a warm partnership letter &mdash; &ldquo;Can I add you to my roster?&rdquo; &mdash; NOT a job offer. We&rsquo;re building the bench BEFORE we need it so when a receiver calls about a 1955-built triplex, we already have an EPA firm we can hand them.</p>
            <p><strong style={{ color: "#86EFAC" }}>How to use it:</strong> Type the firm name + their public email (look up on Google: &ldquo;Fort Worth EPA RRP lead paint contractor&rdquo;). Add a short note if you have a specific job context. Hit Send. The system tracks delivery + opens automatically.</p>
            <p style={{ marginBottom: 0, padding: "8px 12px", background: "rgba(184,145,90,0.12)", border: "1px solid rgba(184,145,90,0.35)", borderRadius: 6 }}>
              <strong style={{ color: "#B8915A" }}>🎤 Talking points if they call you back:</strong> &ldquo;I&rsquo;m building my pre-1978 sub-vendor roster ahead of my August pilot launch. I don&rsquo;t take a referral fee &mdash; you contract with the property owner directly. I just want to make sure when a HUD-VASH veteran lands in a 1962-built triplex, I have a vetted EPA RRP firm I can hand the owner.&rdquo;
            </p>
            <div style={{ marginTop: "1rem", padding: "0.9rem 1rem", background: "rgba(27,42,78,0.55)", border: "1px solid rgba(184,145,90,0.45)", borderRadius: 7 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.7rem" }}>
                <div>
                  <div style={{ color: "#B8915A", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>📚 Outreach Script Library</div>
                  <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.94rem", marginTop: 3 }}>Drop 3 voice-true vendor scripts into your Scripts tab</div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.8rem", marginTop: 4 }}>
                    Baseline hook · Baseline pricing + pre-1978 angle · Small-LLC growth angle. Pull mid-call from the Dialer, or paste into a warm email.
                  </div>
                </div>
                <button type="button" onClick={seedVendorScripts}
                        data-testid="vendor-scripts-seed"
                        disabled={seedStatus === "seeding"}
                        style={{
                          background: "transparent", color: "#B8915A",
                          border: "1px solid #B8915A", padding: "0.6rem 1.1rem",
                          borderRadius: 5, fontWeight: 700, fontSize: "0.85rem",
                          letterSpacing: "0.03em",
                          cursor: seedStatus === "seeding" ? "not-allowed" : "pointer",
                          opacity: seedStatus === "seeding" ? 0.6 : 1,
                        }}>
                  {seedStatus === "seeding" ? "Adding…" : "💬 Seed Vendor Scripts (3)"}
                </button>
              </div>
              {seedResult && seedStatus === "done" && (
                <div data-testid="vendor-seed-result"
                     style={{
                       marginTop: "0.7rem",
                       background: seedResult.error ? "rgba(220, 38, 38, 0.12)" : "rgba(34, 197, 94, 0.12)",
                       border: `1px solid ${seedResult.error ? "rgba(220, 38, 38, 0.4)" : "rgba(34, 197, 94, 0.4)"}`,
                       color: seedResult.error ? "#FCA5A5" : "#86EFAC",
                       padding: "0.6rem 0.85rem", borderRadius: 5, fontSize: "0.83rem",
                     }}>
                  {seedResult.error ? (
                    <>⚠ {seedResult.error}</>
                  ) : (
                    <>
                      ✓ Added <strong>{seedResult.added_count}</strong> vendor script{seedResult.added_count === 1 ? "" : "s"} to your <em>Scripts</em> tab
                      {seedResult.skipped_count > 0 && <> · <strong>{seedResult.skipped_count}</strong> already in library</>}.
                      {seedResult.added && seedResult.added.length > 0 && (
                        <div style={{ marginTop: 4, fontSize: "0.76rem", opacity: 0.85 }}>{seedResult.added.join(" · ")}</div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Compose */}
      <div data-testid="lp-compose" style={{
        background: "rgba(15,23,42,0.6)", border: "1px solid rgba(122,143,126,0.3)",
        borderRadius: 10, padding: "1.1rem 1.3rem"
      }}>
        <div style={{ color: "#86EFAC", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>
          ✉️ Invite a Sub-Vendor (12 trades supported)
        </div>
        <select value={tradeCategory} onChange={(e) => setTradeCategory(e.target.value)}
                data-testid="lp-trade-category"
                style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.75rem", borderRadius: 5, fontSize: "0.88rem", marginBottom: 6 }}>
          {trades.length === 0
            ? <option value="lead_paint">Lead-Paint / EPA RRP</option>
            : trades.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <input type="text" placeholder="Firm name (e.g., DFW Lead Removal Services)"
               value={firmName} onChange={(e) => setFirmName(e.target.value)}
               data-testid="lp-firm-name"
               style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.75rem", borderRadius: 5, fontSize: "0.88rem", marginBottom: 6 }} />
        <input type="email" placeholder="Firm email (e.g., info@example.com)"
               value={firmEmail} onChange={(e) => setFirmEmail(e.target.value)}
               data-testid="lp-firm-email"
               style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.75rem", borderRadius: 5, fontSize: "0.88rem", marginBottom: 6 }} />
        <input type="text" placeholder="Firm phone (for the call dialer)"
               value={firmPhone} onChange={(e) => setFirmPhone(e.target.value)}
               data-testid="lp-firm-phone"
               style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.75rem", borderRadius: 5, fontSize: "0.88rem", marginBottom: 6 }} />
        <textarea placeholder="Notes for your records (optional)"
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  data-testid="lp-notes"
                  style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.75rem", borderRadius: 5, fontSize: "0.86rem", resize: "vertical", fontFamily: "inherit" }} />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button type="button" onClick={sendInvite} disabled={sending}
                  data-testid="lp-send-invite"
                  style={{ background: sending ? "#4B5563" : "#7a8f7e", color: "#f5efe1", border: "1px solid #5e7263", padding: "0.6rem 1.4rem", borderRadius: 5, fontWeight: 700, fontSize: "0.9rem", cursor: sending ? "not-allowed" : "pointer" }}>
            {sending ? "Sending…" : "📤 Send Invitation"}
          </button>
        </div>
      </div>

      {toast && (
        <div data-testid="lp-toast" style={{
          background: toast.ok ? "rgba(34,197,94,0.12)" : "rgba(220,38,38,0.12)",
          border: `1px solid ${toast.ok ? "rgba(34,197,94,0.4)" : "rgba(220,38,38,0.4)"}`,
          color: toast.ok ? "#86EFAC" : "#FCA5A5",
          padding: "0.7rem 1rem", borderRadius: 6, fontSize: "0.9rem"
        }}>
          {toast.ok ? "✓ " : "⚠ "}{toast.msg}
        </div>
      )}

      {/* Roster list */}
      <div>
        <div style={{ color: "#9CA3AF", fontSize: "0.85rem", marginBottom: 6 }}>
          Your roster ({invites.length})
        </div>
        {loading && <div style={{ color: "#9CA3AF" }}>Loading…</div>}
        {!loading && invites.length === 0 && (
          <div style={{ color: "#6B7280", fontStyle: "italic", padding: "0.85rem", border: "1px dashed #374151", borderRadius: 6 }}>
            No invites yet. Send your first one above ↑
          </div>
        )}
        <div style={{ display: "grid", gap: "0.6rem" }}>
          {invites.map(inv => {
            const t = inv.tracking || {};
            const statusKey = t.status || inv.status || "queued";
            const status = STATUS_LABEL[statusKey] || STATUS_LABEL.sent;
            return (
              <div key={inv.invite_id} data-testid={`lp-invite-${inv.invite_id}`} style={{
                background: "rgba(15,23,42,0.5)", border: "1px solid rgba(122,143,126,0.25)",
                borderRadius: 8, padding: "0.85rem 1rem", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.6rem"
              }}>
                <div style={{ flex: "1 1 280px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.95rem" }}>{inv.firm_name}</div>
                    {inv.trade_label && (
                      <span style={{ background: "rgba(184,145,90,0.18)", color: "#B8915A", padding: "0.15rem 0.6rem", borderRadius: 999, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.05em" }}>
                        {inv.trade_label}
                      </span>
                    )}
                  </div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.8rem", marginTop: 2 }}>
                    {inv.firm_email}{inv.firm_phone ? ` · ${inv.firm_phone}` : ""}
                  </div>
                  <div style={{ color: "#6B7280", fontSize: "0.72rem", marginTop: 2 }}>
                    Sent {inv.created_at?.slice(0,16).replace("T"," ")} UTC
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  {inv.firm_phone && (
                    <button type="button" onClick={() => openDialer(inv)}
                            data-testid={`lp-call-${inv.invite_id}`}
                            style={{ background: "transparent", color: "#86EFAC", border: "1px solid #86EFAC", padding: "0.35rem 0.75rem", borderRadius: 5, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}>
                      📞 Call
                    </button>
                  )}
                  <div style={{ padding: "0.3rem 0.7rem", borderRadius: 999, border: `1px solid ${status.color}`, color: status.color, fontSize: "0.78rem", fontWeight: 700 }}>
                    {status.label}{t.open_count > 1 ? ` (${t.open_count}x)` : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {dialerCtx && (
        <CallDialer open={dialerOpen} onClose={() => setDialerOpen(false)} {...dialerCtx} />
      )}
    </div>
  );
};

export default LeadPaintRosterTab;
